import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Truck, FileText, BarChart3, ShieldCheck, Package, Users } from "lucide-react";

const BRAND_BG = "#426db0";
const BOXHERO_LOGO_IMG =
  "https://customer-assets.emergentagent.com/job_32254e95-6993-4c31-846d-f74b6176999f/artifacts/2xvx3yp3_banner%20larbve-4.jpg";
const BOXHERO_BRAND_IMG =
  "https://customer-assets.emergentagent.com/job_32254e95-6993-4c31-846d-f74b6176999f/artifacts/ag9vdnnt_Asset%204.png";

const FEATURES = [
  { icon: Package, title: "Order tracking", desc: "Live status on every order — pending, packed, shipped, delivered." },
  { icon: Truck, title: "Shipment analytics", desc: "Carrier mix, delivery times, on-time performance — at a glance." },
  { icon: FileText, title: "Invoices & receipts", desc: "Open and paid invoices, PDF downloads, and one-click Pay Now." },
  { icon: BarChart3, title: "Spending insights", desc: "How much you spent this month, average cost per order, trends." },
  { icon: ShieldCheck, title: "Secure billing", desc: "Secure hosted payment management — we never store card details." },
  { icon: Users, title: "Team access", desc: "Invite billing, ops, and viewer roles with separate permissions." },
];

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user && user !== false) {
      const to = location.state?.from?.pathname || "/";
      navigate(to, { replace: true });
    }
  }, [user, navigate, location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim().toLowerCase(), password);
    setLoading(false);
    if (!res.ok) setError(res.error || "Login failed");
    else {
      const to = location.state?.from?.pathname || "/";
      navigate(to, { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-white lg:h-screen lg:overflow-hidden lg:grid lg:grid-cols-2">
      {/* LEFT — brand & product story (desktop only) */}
      <div className="hidden lg:flex flex-col text-white relative overflow-hidden h-screen" style={{ backgroundColor: BRAND_BG }}>
        <div className="absolute top-0 left-0 right-0 h-2 bg-bh-orange" />

        <div className="flex justify-center pt-6 pb-1 relative z-10">
          <img src={BOXHERO_LOGO_IMG} alt="BoxHero" className="h-52 xl:h-64 w-auto max-w-full" />
        </div>

        <div className="flex-1 min-h-0 flex flex-col justify-center px-12 relative z-10">
          <h1 className="text-white text-center text-3xl xl:text-4xl font-extrabold leading-tight drop-shadow-sm" style={{ fontFamily: "Manrope" }}>
            Your <span className="text-bh-orange">brand's</span> fulfillment command center.
          </h1>
          <p className="text-white/90 mt-4 text-sm xl:text-base font-medium leading-relaxed max-w-xl mx-auto text-center">
            One branded portal to track every order, monitor shipping performance, manage invoices, and see exactly how you're spending — all in real time.
          </p>

          <div className="mt-7 grid grid-cols-2 gap-x-6 gap-y-4 max-w-xl mx-auto">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="flex items-start gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-white/15 text-white flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-white text-sm leading-tight">{f.title}</p>
                    <p className="text-xs text-white/75 leading-snug mt-0.5">{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="px-12 pb-6 flex items-end justify-between gap-6 relative z-10">
          <p className="text-xs text-white/70 font-medium leading-relaxed">
            © {new Date().getFullYear()} BoxHero Logistics Corp.<br />
            All rights reserved.
          </p>
          <img
            src={BOXHERO_BRAND_IMG}
            alt="Your Brand's Fulfillment Partner · boxhero.com"
            className="w-28 xl:w-32 h-auto"
          />
        </div>

        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-white/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute top-1/3 -right-24 w-72 h-72 bg-bh-orange/15 rounded-full blur-3xl pointer-events-none"></div>
      </div>

      {/* MOBILE hero — compact, readable, scrollable; users see what they're logging into */}
      <div className="lg:hidden text-white relative overflow-hidden" style={{ backgroundColor: BRAND_BG }}>
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-bh-orange" />
        <div className="flex justify-center pt-5 pb-2 relative z-10">
          <img src={BOXHERO_LOGO_IMG} alt="BoxHero" className="h-28 w-auto max-w-full" />
        </div>
        <div className="px-6 pb-7 relative z-10">
          <h1 className="text-white text-center text-2xl font-extrabold leading-tight" style={{ fontFamily: "Manrope" }}>
            Your <span className="text-bh-orange">brand's</span> fulfillment hub.
          </h1>
          <p className="text-white/85 mt-2 text-sm text-center leading-relaxed max-w-md mx-auto">
            Track orders, shipping performance, and invoices in one branded portal — in real time.
          </p>
          <div className="mt-5 grid grid-cols-1 gap-2.5 max-w-md mx-auto">
            {FEATURES.slice(0, 3).map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="flex items-center gap-2.5 bg-white/10 rounded-lg px-3 py-2">
                  <div className="w-8 h-8 rounded-lg bg-white/15 text-white flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4" />
                  </div>
                  <p className="font-semibold text-white text-sm">{f.title}</p>
                </div>
              );
            })}
          </div>
          <p className="text-center text-xs text-white/70 mt-5 animate-bounce">↓ Sign in or create an account below</p>
        </div>
      </div>

      {/* RIGHT — sign-in form */}
      <div className="flex items-start lg:items-center justify-center p-6 lg:p-10 bg-white relative lg:h-screen lg:overflow-y-auto">
        <div className="w-full max-w-md py-6 lg:py-0">
          <h2 className="text-3xl font-bold text-bh-navy-deep" style={{ fontFamily: "Manrope" }}>Welcome back</h2>
          <p className="text-bh-muted mt-2">Sign in to your BoxHero customer portal.</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <label className="block text-sm font-medium text-bh-navy-deep mb-1.5">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@company.com"
                className="w-full px-4 py-3 border border-bh-divider rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-bh-blue/40 focus:border-bh-blue transition-colors text-bh-navy-deep"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-bh-navy-deep mb-1.5">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="w-full px-4 py-3 border border-bh-divider rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-bh-blue/40 focus:border-bh-blue transition-colors text-bh-navy-deep"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3">
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full bg-bh-blue hover:bg-bh-blue-dark active:scale-[0.99] disabled:opacity-60 text-white font-semibold py-3 rounded-lg transition-all"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
            <p className="text-center text-sm">
              <Link to="/reset-password" data-testid="forgot-password-link" className="text-bh-blue hover:underline font-medium">
                Forgot your password?
              </Link>
            </p>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px bg-bh-divider flex-1" />
            <span className="text-xs text-bh-muted uppercase tracking-wide">New to BoxHero?</span>
            <div className="h-px bg-bh-divider flex-1" />
          </div>

          <Link
            to="/register"
            data-testid="go-to-register-btn"
            className="w-full inline-flex items-center justify-center bg-white border-2 border-bh-blue text-bh-blue hover:bg-bh-blue hover:text-white font-semibold py-3 rounded-lg transition-colors"
          >
            Create an account
          </Link>
          <p className="mt-3 text-center text-xs text-bh-muted">
            New accounts are reviewed and approved by the BoxHero team before access is granted.
          </p>

          <p className="mt-8 text-center text-sm text-bh-muted">
            Need help? Contact{" "}
            <a data-testid="login-support-email" href="mailto:3pl@boxhero.com" className="text-bh-blue hover:text-bh-blue-dark font-medium">
              3pl@boxhero.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
